// Special invoices from BidBuyZoom to companies (marketing, setup work...).
// Made here as drafts, then sent: the company gets an email with a Stripe "Pay now" link.
// Rules: backend services/platformInvoices.js

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, post } from "../../utils/apiManager.js";

export const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const date = (v) => (v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export const STATUS = {
  draft: { label: "Draft", color: "#475569", bg: "#f1f5f9" },
  open: { label: "Sent, unpaid", color: "#b45309", bg: "#fef3c7" },
  overdue: { label: "Overdue", color: "#b91c1c", bg: "#fee2e2" },
  paid: { label: "Paid", color: "#047857", bg: "#d1fae5" },
  void: { label: "Void", color: "#64748b", bg: "#f1f5f9" },
  uncollectible: { label: "Uncollectible", color: "#64748b", bg: "#f1f5f9" },
};
export const statusOf = (inv) => (inv.status === "open" && inv.dueDate && new Date(inv.dueDate) < new Date() ? "overdue" : inv.status);
export const pill = (key) => {
  const s = STATUS[key] || STATUS.draft;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, color: s.color, background: s.bg, whiteSpace: "nowrap" }}>{s.label}</span>;
};

export const card = { background: "#fff", borderRadius: 14, border: "1px solid #edf0f7", boxShadow: "0 1px 4px rgba(26,39,68,.05)", marginBottom: 24 };
export const th = { padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9aa5c0", textAlign: "left", borderBottom: "1px solid #edf0f7", background: "#f8f9fc", whiteSpace: "nowrap" };
export const td = { padding: "11px 14px", fontSize: 14, color: "#3a4a6a", borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" };
export const btn = { border: "1px solid #d5dbe8", background: "#fff", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#1a2744" };
export const btnPrimary = { ...btn, background: "#AD2429", borderColor: "#AD2429", color: "#fff" };
export const input = { padding: "8px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, color: "#0a1628", background: "#fff", outline: "none", fontFamily: "inherit" };

const Invoices = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), perPage: "25" });
    if (status) q.set("status", status);
    get(`/company/platform-invoices?${q.toString()}`)
      .then((res) => setData(res?.status ? res.data : null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    get("/company/platform-invoices/settings").then((res) => res?.status && setSettings(res.data));
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    const res = await post("/company/platform-invoices/settings", { taxRate: settings.taxRate, daysUntilDue: settings.daysUntilDue });
    setSavingSettings(false);
    setNotice({ ok: !!res?.status, text: res?.message || "Done." });
    if (res?.status) setSettings((s) => ({ ...s, ...res.data }));
  };

  const totals = data?.totals || {};
  const invoices = data?.invoices || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  const boxes = [
    { key: "draft", title: "Drafts" },
    { key: "open", title: "Sent, unpaid" },
    { key: "paid", title: "Paid" },
    { key: "void", title: "Void" },
  ];

  return (
    <div style={{ padding: "24px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0a1628", margin: 0 }}>Invoices</h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14, maxWidth: 680 }}>
            Bill a company for something special, like marketing. Sending emails the company a link to pay
            on Stripe. It is never charged to their card on file automatically.
          </p>
        </div>
        <button type="button" style={btnPrimary} onClick={() => navigate("/admin/invoices/edit")}>+ New invoice</button>
      </div>

      {notice && (
        <div style={{ ...card, padding: "10px 16px", marginBottom: 16, background: notice.ok ? "#ecfdf5" : "#fef2f2", color: notice.ok ? "#065f46" : "#991b1b" }}>{notice.text}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        {boxes.map((b) => (
          <button key={b.key} type="button" onClick={() => { setStatus(status === b.key ? "" : b.key); setPage(1); }}
            style={{ ...card, marginBottom: 0, padding: "16px 18px", textAlign: "left", cursor: "pointer", outline: status === b.key ? "2px solid #AD2429" : "none" }}>
            <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>{b.title}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>{money(totals[b.key]?.amount)}</div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>{totals[b.key]?.count || 0} invoice{totals[b.key]?.count === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>

      {settings && (
        <div style={{ ...card, padding: "16px 18px", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <div style={{ fontWeight: 700, color: "#0a1628", width: "100%", marginBottom: -4 }}>Invoice settings</div>
          <label style={{ fontSize: 13, color: "#475569" }}>
            Sales tax %<br />
            <input type="number" min="0" max="25" step="0.001" value={settings.taxRate}
              onChange={(e) => setSettings({ ...settings, taxRate: e.target.value })} style={{ ...input, width: 110 }} />
          </label>
          <label style={{ fontSize: 13, color: "#475569" }}>
            Days to pay<br />
            <input type="number" min="0" max="365" value={settings.daysUntilDue}
              onChange={(e) => setSettings({ ...settings, daysUntilDue: e.target.value })} style={{ ...input, width: 110 }} />
          </label>
          <button type="button" style={btn} disabled={savingSettings} onClick={saveSettings}>{savingSettings ? "Saving…" : "Save settings"}</button>
          <div style={{ fontSize: 12, color: "#94a3b8", flex: 1, minWidth: 220 }}>
            Filled in on new invoices (each invoice can be changed). Sales tax 0 = no tax. Next number: {settings.nextNumber}
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "12px 14px" }}>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={input}>
            <option value="">All invoices</option>
            <option value="draft">Drafts</option>
            <option value="open">Sent, unpaid</option>
            <option value="overdue">Overdue</option>
            <option value="paid">Paid</option>
            <option value="void">Void</option>
          </select>
          {loading && <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading…</span>}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Invoice</th>
                <th style={th}>Company</th>
                <th style={{ ...th, textAlign: "right" }}>Total</th>
                <th style={th}>Status</th>
                <th style={th}>Sent</th>
                <th style={th}>Due</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv._id}>
                  <td style={{ ...td, fontWeight: 600 }}>{inv.number || "Draft"}</td>
                  <td style={td}>{inv.companyName}<div style={{ fontSize: 12, color: "#94a3b8" }}>{inv.lines?.[0]?.description}{inv.lines?.length > 1 ? ` + ${inv.lines.length - 1} more` : ""}</div></td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{money(inv.total)}</td>
                  <td style={td} title={inv.lastError || ""}>{pill(statusOf(inv))}{inv.lastError ? " ⚠" : ""}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{date(inv.sentAt)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{inv.status === "open" ? date(inv.dueDate) : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button type="button" style={{ ...btn, padding: "4px 10px", fontSize: 12 }} onClick={() => navigate(`/admin/invoices/edit?id=${inv._id}`)}>
                      {inv.status === "draft" ? "Edit" : "Open"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && invoices.length === 0 && (
                <tr><td style={{ ...td, textAlign: "center", color: "#94a3b8" }} colSpan={7}>No invoices yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: 12 }}>
            <button type="button" style={btn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span style={{ fontSize: 13, color: "#64748b", alignSelf: "center" }}>Page {page} of {pages}</span>
            <button type="button" style={btn} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
